import axios from "axios";
import {
    AuthModel,
    UpsertUserDTO,
    UpsertUserDTOSchema,
    UserModel,
    UserSchema,
} from "../types/UserModel";
import qs from "qs";
import { toCamelCase, toSnakeCase } from "../utils/objectHelper";
import { PBN } from "../types/PBN";
import { SimpleFormSelectOption } from "../components/form/SimpleFormSelect";

export enum UserRole {
    ROLE_SUPER_ADMIN = "ROLE_SUPER_ADMIN",
    ROLE_ADMIN = "ROLE_ADMIN",
    ROLE_LEAD_PROJECT_MANAGER = "ROLE_LEAD_PROJECT_MANAGER",
    ROLE_LEAD_BUSINESS_MANAGER = "ROLE_LEAD_BUSINESS_MANAGER",
    ROLE_LEAD_LINKER = "ROLE_LEAD_LINKER",
    ROLE_LEAD_WRITER = "ROLE_LEAD_WRITER",
    ROLE_LEAD_PROOFREADER = "ROLE_LEAD_PROOFREADER",
    ROLE_LEAD_WORKER = "ROLE_LEAD_WORKER",
    ROLE_PROJECT_MANAGER = "ROLE_PROJECT_MANAGER",
    ROLE_BUSINESS_MANAGER = "ROLE_BUSINESS_MANAGER",
    ROLE_LINKER = "ROLE_LINKER",
    ROLE_WRITER = "ROLE_WRITER",
    ROLE_PROOFREADER = "ROLE_PROOFREADER",
    ROLE_WORKER = "ROLE_WORKER",
    ROLE_LEAD_WEBMASTER = "ROLE_LEAD_WEBMASTER",
    ROLE_WEBMASTER = "ROLE_WEBMASTER",
}
export type TUserRole = keyof typeof UserRole;

type UserResponse = {
    id: number;
    email: string;
    first_name: string;
    last_name: string;
    role: string;
};

export const UserTransport = {
    getUser: async (): Promise<UserModel> => {
        const { data } = await axios.get<UserModel>("/verify_token", {});

        return UserSchema.cast(toCamelCase(data), { stripUnknown: true });
    },

    login: async (email: string, password: string): Promise<AuthModel> => {
        const { data } = await axios.post<AuthModel>("/login_check", {
            username: email,
            password,
        });

        return data;
    },

    listByRoles: async (params: object) => {
        const { data } = await axios.get<UserResponse[]>(`/users?${qs.stringify(params)}`, {});

        return data.map((user) => UserSchema.cast(toCamelCase(user), { stripUnknown: true }));
    },

    listOptions: async (params: object) => {
        const { data } = await axios.get<SimpleFormSelectOption[]>(
            `/listOptions?${qs.stringify(params)}`,
            {}
        );

        return data;
    },

    findAllLead: async () => {
        const { data } = await axios.get<UserResponse[]>("/usersLead");
        return data.map((user) => UserSchema.cast(toCamelCase(user), { stripUnknown: true }));
    },

    usersUnderManagement: async () => {
        const { data } = await axios.get<UserResponse[]>("/users/underManagement");
        return data.map((user) => UserSchema.cast(toCamelCase(user), { stripUnknown: true }));
    },

    toggleActive: async (user: UserModel): Promise<void> => {
        const action = "toggleActive";
        await axios.patch<UserModel>(`/users/${user.id}/actions/${action}`);
    },

    save: async (object: UpsertUserDTO, id: number | undefined | null): Promise<UserModel> => {
        const response = await axios.request<UserModel>({
            method: id ? "put" : "post",
            url: id ? `/users/${id}` : "/users",
            data: toSnakeCase(UpsertUserDTOSchema.cast(object, { stripUnknown: true })),
        });

        return UserSchema.cast(toCamelCase(response.data), { stripUnknown: true });
    },
    formTransformer: (object: UserModel): UpsertUserDTO | null => {
        if (!object) {
            return null;
        }

        return {
            email: object.email,
            firstName: object.firstName,
            lastName: object.lastName,
            lang: object.lang,
            role: object.role,
            phoneNumber: object.phoneNumber,
            orderType: object.orderType,
            quota: object.quota,
            hasDuplicateContentCheck: object.hasDuplicateContentCheck,
            priceHomePage: object.priceHomePage,
            writingType: object.writingType,
            priceBlog: object.priceBlog,
            priceProductPage: object.priceProductPage,
            priceLandingPage: object.priceLandingPage,
            priceOptimizedExtra: object.priceOptimizedExtra,
            priceWrite: object.priceWrite,
            priceAiAuto: object.priceAiAuto,
            priceAiManual: object.priceAiManual,
            priceTranslate: object.priceTranslate,
            priceRewrite: object.priceRewrite,
            priceExpired: object.priceExpired,
            id: object.id,
            password: object.password,
            passwordConfirmation: object.passwordConfirmation,
            leadId: object.lead?.id,
            pbnIds: object.pbns?.map((pbn: PBN) => pbn.id?.toString()),
        };
    },

    requestPassword: (email: string) => {
        return axios.post<{ result: boolean }>("/request-password", {
            email,
        });
    },
};
